import { Injectable } from '@angular/core';
import { OrganisationNode } from '@coin/shared/org-tree/util';
import { Position, SpecialRole, SpecialRoleType } from '@coin/shared/core/util-models';
import { ImmerComponentStore } from 'ngrx-immer/component-store';
import { Observable } from 'rxjs';

interface OrgTreeStateModel {
  organisationNodes: { [organisationId: string]: OrganisationNode };
  employeeRoles: { [organisationId: string]: SpecialRole[] };
  positions: { [organisationId: string]: Position[] };
  positionsLoading: { [organisationId: string]: boolean };
  currentOrganisationId?: string;
  selectedOrganisationId?: string;
}

const initialState = (): OrgTreeStateModel => ({
  organisationNodes: {},
  employeeRoles: {},
  positions: {},
  positionsLoading: {}
});

@Injectable()
export class OrgTreeComponentState extends ImmerComponentStore<OrgTreeStateModel> {
  public currentOrganisationNode$: Observable<OrganisationNode> = this.select(
    ({ currentOrganisationId, organisationNodes }) => organisationNodes[currentOrganisationId]
  );

  public currentOrganisationEmployeeRoles$: Observable<SpecialRole[]> = this.select(
    ({ currentOrganisationId, employeeRoles }) => employeeRoles[currentOrganisationId]
  );

  public currentOrganisationBusinessPartners$: Observable<SpecialRole[]> = this.select(
    this.currentOrganisationEmployeeRoles$,
    employeeRoles => employeeRoles?.filter(role => role.type === SpecialRoleType.BusinessPartner) ?? []
  );

  public currentOrganisationSpecialResponsibilities$: Observable<SpecialRole[]> = this.select(
    this.currentOrganisationEmployeeRoles$,
    employeeRoles => employeeRoles?.filter(role => role.type === SpecialRoleType.SpecialResponsibility) ?? []
  );

  public currentOrganisationCountryResponsibilities$: Observable<SpecialRole[]> = this.select(
    this.currentOrganisationEmployeeRoles$,
    employeeRoles => employeeRoles?.filter(role => role.type === SpecialRoleType.CountryResponsibility) ?? []
  );

  public currentOrganisationHasSpecialRoles$: Observable<boolean> = this.select(
    this.currentOrganisationEmployeeRoles$,
    employeeRoles => employeeRoles?.length > 0
  );

  public selectedOrganisationId$: Observable<string> = this.select(
    ({ selectedOrganisationId }) => selectedOrganisationId
  );

  public selectedOrganisationNode$: Observable<OrganisationNode> = this.select(
    ({ selectedOrganisationId, organisationNodes }) => organisationNodes[selectedOrganisationId]
  );

  public selectedOrganisationEmployeeRoles$: Observable<SpecialRole[]> = this.select(
    ({ selectedOrganisationId, employeeRoles }) => employeeRoles[selectedOrganisationId]
  );

  public selectedOrganisationBusinessPartners$: Observable<SpecialRole[]> = this.select(
    this.selectedOrganisationEmployeeRoles$,
    employeeRoles => employeeRoles?.filter(role => role.type === SpecialRoleType.BusinessPartner) ?? []
  );

  public selectedOrganisationSpecialResponsibilities$: Observable<SpecialRole[]> = this.select(
    this.selectedOrganisationEmployeeRoles$,
    employeeRoles => employeeRoles?.filter(role => role.type === SpecialRoleType.SpecialResponsibility) ?? []
  );

  public selectedOrganisationCountryResponsibilities$: Observable<SpecialRole[]> = this.select(
    this.selectedOrganisationEmployeeRoles$,
    employeeRoles => employeeRoles?.filter(role => role.type === SpecialRoleType.CountryResponsibility) ?? []
  );

  public selectedOrganisationHasSpecialRoles$: Observable<boolean> = this.select(
    this.selectedOrganisationEmployeeRoles$,
    employeeRoles => employeeRoles?.length > 0
  );

  public selectedOrganisationNodeHasChildren$: Observable<boolean> = this.select(
    this.selectedOrganisationNode$,
    this.selectedOrganisationHasSpecialRoles$,
    (selectedOrganisationNode, hasSpecialRoles) => selectedOrganisationNode?.children?.length > 0 || hasSpecialRoles
  );

  constructor() {
    super(initialState());
  }

  public getOrganisationNode(organisationId: string): OrganisationNode {
    return this.get().organisationNodes[organisationId];
  }

  public setCurrentOrganisationId(organisationId: string): void {
    this.setState(state => {
      if (state.currentOrganisationId !== organisationId) {
        state.currentOrganisationId = organisationId;
      }
    });
  }

  public setOrganisationNodes(organisationNodes: { [organisationId: string]: OrganisationNode }): void {
    this.setState(state => {
      state.organisationNodes = organisationNodes;
    });
  }

  public setEmployeeRoles(employeeRoles: { [organisationId: string]: SpecialRole[] }): void {
    this.setState(state => {
      state.employeeRoles = employeeRoles;
    });
  }

  public setPositions(positions: { [organisationId: string]: Position[] }): void {
    this.setState(state => {
      state.positions = positions;
    });
  }

  public setPositionsLoading(positionsLoading: { [organisationId: string]: boolean }): void {
    this.setState(state => {
      state.positionsLoading = positionsLoading;
    });
  }

  public setSelectedOrganisationId(organisationId: string): void {
    this.setState(state => {
      if (state.selectedOrganisationId !== organisationId) {
        state.selectedOrganisationId = organisationId;
      }
    });
  }

  public selectPositions(organisationId: string): Observable<Position[]> {
    return this.select(({ positions }) => positions[organisationId]);
  }

  public selectPositionsLoading(organisationId: string): Observable<boolean> {
    return this.select(({ positionsLoading }) => positionsLoading[organisationId]);
  }
}
